/**
 * @ngdoc service
 * @name flowModelImportService
 * @module flowingly.runner.services
 * Provides the public interface for the various file-type-specific importers.
 */

import FlowModelXmlParser from './flow.model.parsers/flow.model.xml.parser';
import { IPromise } from 'angular';
import IPublishWorkflowModel from '@Shared.Angular/@types/core/api/models/publishWorkflowModel';
import ISaveFlowModelOwnerModel from '@Shared.Angular/@types/core/api/models/saveFlowModelOwnerModel';
import INodeCommandData from '@Shared.Angular/@types/core/contracts/commands/modeler/nodeCommandData';
import IGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/group/groupDetail';
import FlowModelAttachmentZipParser from './flow.model.parsers/flow.model.attachment.zip.parser';
import {
  ActorTypes,
  FlowinglyRoles,
  FlowModelPublishType,
  FormFieldType,
  FormFieldTypePascal,
  NodeCategory,
  SearchEntityType
} from '../../../Flowingly.Shared.Angular/src/flowingly.services/flowingly.constants';
import FlowModelAttachmentUnsortedZipParser from './flow.model.parsers/flow.model.attachment.unsorted.zip.parser';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { IFlowModelSummary } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSummary';
import { IUserSummary } from '@Shared.Angular/@types/core/contracts/queryModel/user/userSummary';
import IRoleDetail from '@Shared.Angular/@types/core/contracts/queryModel/authorisation/roleDetail';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import { IModelerNodeLink } from '@Shared.Angular/@types/workflow';
import FlowModelVsdxParser from './flow.model.parsers/flow.model.vsdx.parser';
import { EmptyGuid, Guid } from '@Shared.Angular/@types/guid';
import { FileFolder } from '@Shared.Angular/flowingly.services/file.service';
import IGateway from '@Shared.Angular/@types/core/contracts/queryModel/workflow/gateways/gateway';
import { EventDimension } from '@Shared.Angular/flowingly.bpmn.modeler/flowingly.bpmn.constants';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IModelerNode from '@Shared.Angular/@types/modelerNode';
import IFlowModelCommandData from '@Shared.Angular/@types/core/contracts/commands/modeler/flowModelCommandData';
import IModelerProcessDetailDto from '@Shared.Angular/@types/core/contracts/queryModel/modeler/modelerProcessDetailDto';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';

export interface IImportable {
  key: string;
  willImport: boolean;
  state?: ImportableFlowModelState;
  importMessage?: string;
}

export interface IFlowModelImportOptions {
  createTeamsInFlowingly?: boolean;
  useReferenceNo?: boolean;
  preferExpert?: boolean;
}

export interface IImportableCategory {
  name: string;
  description?: string;
}

export class ImportableFlowModel implements IImportable {
  key: string;
  willImport: boolean;
  state?: ImportableFlowModelState;
  importMessage?: string;
  publish: boolean;

  uniqueId: Guid;
  name: string;
  sourceProcessName?: string | null;
  processReferenceNo?: string | null;
  existingFlowModelId?: Guid;
  description: string;
  background: string;
  processInput: string;
  processOutput: string;
  processOwnerName: string | null;
  processOwnerAttributeName?: string | null;
  processExpertAttributeName?: string | null;
  unresolvedProcessOwnerNames: string[];
  processOwnerId?: Guid;
  modelNodes: IImportableModelNode[];
  modelLinks: IModelerNodeLink[];
  flowinglyNodes: INodeCommandData[];
  categoryHierarchy: IImportableCategory[];
  categoryId?: Guid;
  teams: IGroupDetail[];
  file?: File;
  fileId?: Guid;
  attachments?: IImportableFlowModelAttachment[];
  triggerInput: string;

  public constructor(properties?: Partial<ImportableFlowModel>) {
    Object.assign(this, properties);
    this.unresolvedProcessOwnerNames =
      properties?.unresolvedProcessOwnerNames || [];
  }
}

export default interface IImportableModelNode {
  Card?: { formElements: unknown[] };
  IsFinal?: 'true' | 'false';
  IsInitial?: 'true' | 'false';
  actor?: string;
  actorName?: string;
  actorType?: ActorTypes;
  allowNamingLinks?: boolean;
  avatarUrl?: string;
  category: NodeCategory;
  color?: string;
  dynamicActorType?: number;
  eventDimension?: EventDimension;
  eventType?: number;
  gateway?: IGateway;
  group?: number | string;
  id: Guid;
  isGroup?: 'true' | 'false';
  item?: string;
  key: number;
  loc: string;
  size?: string;
  taskType?: ActivityTaskType;
  text: string;
  tagNames?: string[];
}

export class ImportableFlowModelAttachments implements IImportable {
  key: string;
  willImport: boolean;
  state?: ImportableFlowModelState;
  importMessage?: string;

  attachments: IImportableFlowModelAttachment[];

  constructor(flowModelUniqueId: string) {
    this.key = flowModelUniqueId;
    this.willImport = false;
    this.attachments = [];
    this.state = ImportableFlowModelState.Pending;
  }
}

export enum ImportableFlowModelState {
  Pending,
  Success,
  Failure
}

export interface IImportableFlowModelFile {
  file: File;
  handled: boolean;
}

export interface IImportableFlowModelAttachment {
  name: string;
  htmlEncodedName: string;
  bytes: Uint8Array;
}

export interface IFlowModelParser {
  parseImportables(
    files: IImportableFlowModelFile[],
    options?: IFlowModelImportOptions
  ): Promise<IImportable[]>;
}

class FlowModelImportService {
  static $inject = [
    'workflowModelFactory',
    'guidService',
    'modelerApiService',
    'avatarService',
    'fileService',
    'categoryApiService',
    'flowModelApiService',
    'teamApiService',
    'fflate',
    'notificationService',
    'userApiService',
    'roleApiService',
    'flowModelOwnerDialogService',
    'tagGroupApiService',
    'tagService',
    'APP_CONFIG',
    'flowModelNodeSequenceService'
  ];

  private parsers: IFlowModelParser[];

  constructor(
    private workflowModelFactory: SharedAngular.WorkflowModelFactory,
    private guidService: SharedAngular.GuidService,
    private modelerApiService: SharedAngular.ModelerApiService,
    private avatarService: SharedAngular.AvatarService,
    private fileService: SharedAngular.FileService,
    private categoryApiService: SharedAngular.CategoryApiService,
    private flowModelApiService: SharedAngular.FlowModelApiService,
    private teamApiService: SharedAngular.TeamApiService,
    private fflate: Fflate,
    private notificationService: SharedAngular.NotificationService,
    private userApiService: SharedAngular.UserApiService,
    private roleApiService: RoleApiService,
    private flowModelOwnerDialogService: FlowModelOwnerDialogService,
    private tagGroupApiService: SharedAngular.TagGroupApiService,
    private tagService: TagService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private flowModelNodeSequenceService: SharedAngular.FlowModelNodeSequenceService
  ) {
    this.parsers = [
      new FlowModelXmlParser(
        guidService,
        this.avatarService,
        notificationService
      ),
      new FlowModelAttachmentZipParser(this.fflate, guidService), // This must run before the UnsortedZipParser
      new FlowModelAttachmentUnsortedZipParser(this.fflate),
      new FlowModelVsdxParser(
        this.fflate,
        guidService,
        this.avatarService,
        this.notificationService
      )
    ];
  }

  getImportableFlowModels(files: File[], options?: IFlowModelImportOptions) {
    const importFiles = files.map((file) => {
      return { file, handled: false } as IImportableFlowModelFile;
    });
    const parserPromises = this.getImportablesFromParser(
      this.parsers,
      importFiles,
      options
    );
    return parserPromises.then((importables) => {
      this.applyImportOptions(importables, options);
      const uniqueImportables = new Map(
        importables.flat().map((i) => [i.key, i])
      );
      return [...uniqueImportables.values()];
    });
  }

  private getImportablesFromParser(
    importers: IFlowModelParser[],
    files: IImportableFlowModelFile[],
    options?: IFlowModelImportOptions,
    importerIndex = 0
  ): IPromise<IImportable[]> {
    if (importerIndex >= importers.length) {
      return Promise.resolve<ImportableFlowModel[]>([]);
    }
    const importer = importers[importerIndex];
    return importer.parseImportables(files, options).then((results) => {
      const unhandledFiles = files.filter((importFile) => !importFile.handled);
      return this.getImportablesFromParser(
        importers,
        unhandledFiles,
        options,
        ++importerIndex
      ).then((nextResults) => results.concat(nextResults));
    });
  }

  async importFlowModels(
    importables: ImportableFlowModel[],
    updateCallback: (x: ImportableFlowModel[]) => void,
    options?: IFlowModelImportOptions
  ) {
    this.applyImportOptions(importables, options);
    const createTeamsInFlowingly = options?.createTeamsInFlowingly ?? true;
    const existingCategoriesLowerCase = (
      await this.categoryApiService.getCategories()
    ).map((category) => {
      category.name = category.name.toLowerCase();
      return category;
    });
    let flowModelWasImported = false;
    const tagGroups = await this.getUpdatedTagGroups(importables);
    for (const importable of importables) {
      if (
        !importable.willImport ||
        importable.state !== ImportableFlowModelState.Pending
      ) {
        continue;
      }
      if (importable.categoryHierarchy?.length === 1) {
        // If this importable didn't come with a hierarchy then we need to see
        // if its Category exists anywhere in the hierarchy before we create it
        importable.categoryId = this.findIdOfExistingCategory(
          existingCategoriesLowerCase,
          importable.categoryHierarchy[0].name.toLowerCase()
        );
      }
      if (!importable.categoryId) {
        importable.categoryId = await this.createMissingCategories(
          existingCategoriesLowerCase,
          importable.categoryHierarchy
        );
      }

      if (createTeamsInFlowingly && importable.teams?.length > 0) {
        await this.createMissingTeams(importable.teams);
      }

      if (!importable.modelNodes || importable.modelNodes.length === 0) {
        importable.state = ImportableFlowModelState.Success;
        updateCallback(importables);
        continue;
      }
      await this.importFlowModel(importable, tagGroups)
        .then(() => {
          importable.state = ImportableFlowModelState.Success;
          flowModelWasImported = true;
          if (importable.fileId) {
            importables
              .filter((flowModel) => flowModel.file === importable.file)
              .forEach((flowModel) => (flowModel.fileId = importable.fileId));
          }
          updateCallback(importables);
        })
        .catch((result) => {
          importable.state = ImportableFlowModelState.Failure;
          importable.importMessage =
            result.data?.Message || 'Internal Server Error';
          updateCallback(importables);
        });
    }
    if (flowModelWasImported) {
      await this.flowModelApiService.linkImportedFlowModelComponents(
        FlowModelPublishType.PROCESS_MAP
      );
      await this.updateFlowModelOwners(importables);
    }
  }

  async updateFlowModelOwners(importables: ImportableFlowModel[]) {
    let flowModels: IFlowModelSummary[];
    const getFlowModels = this.flowModelApiService
      .getFlowModels()
      .then((result) => {
        flowModels = result;
      });
    let flowModelAdminRoleId: Guid;
    const getRoles = this.roleApiService.getRoles(false).then((roles) => {
      flowModelAdminRoleId = roles.find(
        (role) => role.name === FlowinglyRoles.FLOW_MODEL_ADMINISTRATOR
      ).id;
    });
    let users: IUserSummary[];
    const getUsers = this.userApiService.getUsers(false).then((result) => {
      users = result;
    });

    await Promise.all([getFlowModels, getRoles, getUsers]);

    const ownerAssignments = importables
      .map((importable) => {
        const flowModel = flowModels.find((fm) => fm.name === importable.name);
        if (!flowModel) {
          return null;
        }
        const user = this.resolveImportableOwnerFromUsers(importable, users);
        if (!user) {
          return null;
        }
        return {
          flowModelId: flowModel.id,
          user
        };
      })
      .filter(Boolean) as { flowModelId: Guid; user: IUserSummary }[];

    const usersById = new Map<Guid, IUserSummary>();
    const flowModelIdsByUserId = new Map<Guid, Guid[]>();
    for (const assignment of ownerAssignments) {
      usersById.set(assignment.user.id, assignment.user);
      const flowModelIdsForUser =
        flowModelIdsByUserId.get(assignment.user.id) || [];
      if (!flowModelIdsForUser.includes(assignment.flowModelId)) {
        flowModelIdsForUser.push(assignment.flowModelId);
      }
      flowModelIdsByUserId.set(assignment.user.id, flowModelIdsForUser);
    }

    await Promise.all(
      Array.from(flowModelIdsByUserId.entries()).map(
        async ([userId, flowModelIds]) => {
          const user = usersById.get(userId);
          if (!user) {
            return;
          }
          await this.ensureUserHasFlowModelAdminRole(
            user,
            flowModelAdminRoleId
          );
          await Promise.all(
            flowModelIds.map((flowModelId) =>
              this.updateFlowModelOwner(flowModelId, user)
            )
          );
        }
      )
    );
  }

  private async updateFlowModelOwner(flowModelId: Guid, user: IUserSummary) {
    await this.flowModelOwnerDialogService
      .getExistingFlowModelOwners(flowModelId)
      .then(async (response) => {
        const owners = response.data;
        if (owners?.some((owner) => owner.id === user.id)) {
          return;
        }
        const userIds =
          owners
            ?.filter(
              (owner) => owner.searchEntityType === SearchEntityType.USER
            )
            .map((owner) => owner.id) || [];
        userIds.push(user.id);
        const saveOwnerModel: ISaveFlowModelOwnerModel = {
          flowModelId: flowModelId,
          userIds: userIds,
          groupIds:
            owners
              ?.filter(
                (owner) => owner.searchEntityType === SearchEntityType.GROUP
              )
              .map((owner) => owner.id) || []
        };
        await this.flowModelOwnerDialogService.saveFlowModelOwners(
          saveOwnerModel
        );
      });
  }

  private async ensureUserHasFlowModelAdminRole(
    user: IUserSummary,
    flowModelAdminRoleId: Guid
  ): Promise<void> {
    if (user.roles?.some((role) => role.id === flowModelAdminRoleId)) {
      return;
    }

    const updatedUser: IUserSummary = {
      ...user,
      roles: [
        ...(user.roles || []),
        { id: flowModelAdminRoleId } as IRoleDetail
      ]
    };

    await this.userApiService.editUser(updatedUser);
    user.roles = updatedUser.roles;
  }

  private async createMissingTeams(teams: IGroupDetail[]) {
    const existingTeams = (await this.teamApiService.getTeamsWithOptions({}))
      .teams;
    for (const team of teams) {
      if (
        existingTeams.some((existingTeam) => existingTeam.name === team.name)
      ) {
        continue;
      }
      await this.teamApiService.saveTeam(team).catch(() => null);
    }
  }

  private async getUpdatedTagGroups(
    importables: ImportableFlowModel[]
  ): Promise<ITagGroupDetail[] | undefined> {
    if (
      !this.APP_CONFIG.enableTagging ||
      !importables.some((importable) =>
        importable.modelNodes?.some((n) => n.tagNames?.length > 0)
      )
    ) {
      return;
    }
    const importerTagGroupName = 'Imported Tags';
    const ignoreCache = true;
    const tagGroups = await this.tagGroupApiService.getAllTagGroups(
      ignoreCache
    );
    const importedTagsGroup = tagGroups.find(
      (group) => group.name === importerTagGroupName
    );
    const normalizedTagNames = importables
      .flatMap(
        (importable) =>
          importable.modelNodes?.flatMap((node) => node.tagNames) || []
      )
      .filter(Boolean)
      .map(this.tagService.normalizeTagName);
    const allImportableTagNames = Array.from(
      new Set<string>(normalizedTagNames)
    );
    const newTagNames = allImportableTagNames.filter(
      (tagName) =>
        !tagGroups.some((group) =>
          group.tags.some(
            (tag) => tag.name.toLowerCase() === tagName.toLowerCase()
          )
        )
    );
    if (newTagNames.length !== 0) {
      if (importedTagsGroup) {
        importedTagsGroup.tags.push(
          ...newTagNames.map((tagName) => ({ name: tagName }))
        );
        await this.tagGroupApiService.save(importedTagsGroup);
      } else {
        const newTagGroup: ITagGroupDetail = {
          id: EmptyGuid,
          description: 'Tags created by Flow Model importer',
          color: '42A5F5',
          name: importerTagGroupName,
          tags: newTagNames.map((tagName) => ({ name: tagName }))
        };
        await this.tagGroupApiService.save(newTagGroup);
      }
    }
    return await this.tagGroupApiService.getAllTagGroups(ignoreCache);
  }

  private findIdOfExistingCategory(
    existingCategoriesLowerCase: ICategoryDetail[],
    categoryNameLowerCase: string
  ) {
    if (!existingCategoriesLowerCase || !categoryNameLowerCase) {
      return;
    }
    for (const existingCategory of existingCategoriesLowerCase) {
      if (existingCategory.name === categoryNameLowerCase) {
        return existingCategory.id;
      }
      const existingSubCategoryId = this.findIdOfExistingCategory(
        existingCategory.subCategories,
        categoryNameLowerCase
      );
      if (existingSubCategoryId) {
        return existingSubCategoryId;
      }
    }
  }

  private async createMissingCategories(
    existingCategoriesLowerCase: ICategoryDetail[],
    categoryHierarchy: IImportableCategory[],
    parent?: ICategoryDetail
  ) {
    if (!categoryHierarchy || categoryHierarchy.length === 0) {
      return parent?.id;
    }
    const category = categoryHierarchy[0];
    const categoryNameLowerCase = category.name.toLowerCase();
    const existingCategory = existingCategoriesLowerCase?.find(
      (existing) => existing.name === categoryNameLowerCase
    );
    if (existingCategory) {
      return this.createMissingCategories(
        existingCategory.subCategories.map((category) => {
          return {
            id: category.id,
            name: category.name.toLowerCase(),
            subCategories: category.subCategories
          } as ICategoryDetail;
        }),
        categoryHierarchy.slice(1),
        existingCategory
      );
    }
    const newCategory: ICategoryDetail = {
      name: category.name,
      description: category.description || '',
      subCategories: []
    };
    if (parent) {
      newCategory.parentId = parent.id;
    }
    const addCategoryResult = await this.categoryApiService.addCategory(
      newCategory
    );
    if (!addCategoryResult.data.success) {
      return; // Failed to make missing Category, so the Flow Model can be created with no Category
    }
    newCategory.id = addCategoryResult.data.dataModel;
    if (parent) {
      parent.subCategories.push(newCategory);
    }

    existingCategoriesLowerCase.push({
      id: newCategory.id,
      name: newCategory.name.toLowerCase(),
      subCategories: newCategory.subCategories
    });

    return this.createMissingCategories(
      newCategory.subCategories,
      categoryHierarchy.slice(1),
      newCategory
    );
  }

  private async createAndLinkAttachments(importable: ImportableFlowModel) {
    const regex = /<p>\[([a-zA-Z]{4,11})\|([^|]+)\|[a-zA-Z0-9-]{36}\]<\/p>/g;
    const videoRegex = /<p>\[(Video)\|([^|]+)\|[^\]]+\]<\/p>/g;
    const imageExtensions = ['.jpg', 'jpeg', '.bmp', '.png', '.webp', '.gif'];
    for (const attachment of importable.attachments || []) {
      const file = new File(
        [new Uint8Array(attachment.bytes)],
        attachment.name
      );
      const lowerCaseAttachmentName = attachment.name.toLowerCase();
      const isImage = imageExtensions.some((ext) =>
        lowerCaseAttachmentName.endsWith(ext)
      );

      let fileId: Guid;

      try {
        const uploadResult = isImage
          ? await this.fileService.uploadEmbeddedImage(
              file,
              FileFolder.StepFiles
            )
          : await this.fileService.uploadAttachDocumentFile(file);
        fileId = uploadResult.data;
      } catch {
        continue;
      }

      for (const node of importable.flowinglyNodes) {
        for (const instruction of node.Card.formElements.filter(
          (f) => f.type === FormFieldType.INSTRUCTION
        )) {
          const matches = [...instruction.value.matchAll(regex)].concat([
            ...instruction.value.matchAll(videoRegex)
          ]);
          const relevantMatches = matches.filter(
            (match) =>
              match[2] === attachment.htmlEncodedName ||
              this.normalizeForComparison(match[1], match[2]) ===
                this.normalizeForComparison(
                  match[1],
                  attachment.htmlEncodedName
                )
          );
          if (relevantMatches.length === 0) {
            continue;
          }
          let replacement = '';
          if (relevantMatches[0][1] === 'Image') {
            replacement = `<img data-file-id="${fileId}" />`;
          } else {
            this.addAttachedDocument(node.Card.formElements, fileId);
          }
          for (const match of relevantMatches) {
            instruction.value = instruction.value.replaceAll(
              match[0],
              replacement
            );
          }
        }
      }
    }
  }

  private addAttachedDocument(formElements, fileId: Guid) {
    let attachmentsField = formElements.find(
      (field) => field.type === FormFieldType.ATTACH_DOCUMENT
    );
    if (attachmentsField === undefined) {
      attachmentsField = {
        attachDocumentFileIds: [],
        conditions: [],
        defaultValueOption: 'none',
        displayName: 'Attach Documents',
        id: this.guidService.new(),
        name: 'field' + new Date().valueOf(),
        noLabel: true,
        type: FormFieldType.ATTACH_DOCUMENT,
        typeName: FormFieldTypePascal.ATTACH_DOCUMENT
      };
      formElements.push(attachmentsField);
    }
    attachmentsField.attachDocumentFileIds.push(fileId);
  }

  private async importFlowModel(
    importable: ImportableFlowModel,
    tagGroups?: ITagGroupDetail[]
  ) {
    await this.createAndLinkAttachments(importable);
    return this.workflowModelFactory
      .createFlowModel(importable.name, importable.description)
      .then(async (flowModel) => {
        await this.resolveImportableOwnerForBusiness(importable);
        return flowModel;
      })
      .then((flowModel) => {
        const processDetailId =
          flowModel.ProcessDetail?.Id &&
          flowModel.ProcessDetail.Id !== EmptyGuid
            ? flowModel.ProcessDetail?.Id
            : null;
        const processOwnerId =
          importable.processOwnerId && importable.processOwnerId !== EmptyGuid
            ? importable.processOwnerId
            : null;
        const processDetail: IModelerProcessDetailDto = {
          id: processDetailId,
          background: importable.background,
          processInput: importable.processInput,
          processOutput: importable.processOutput,
          processOwnerId: importable.processOwnerId,
          triggerInput: importable.triggerInput,
          objective: '',
          processOwner: processOwnerId
            ? {
                id: processOwnerId,
                fullName: importable.processOwnerName
              }
            : null
        };
        const schema = {
          class: 'gl.GraphLinksModel',
          modelData: { position: '-550 -200' },
          nodeDataArray: this.convertImportableNodesToModelNodes(
            importable.modelNodes,
            tagGroups
          ),
          linkDataArray: importable.modelLinks
        };

        this.flowModelNodeSequenceService.calculateNodesRefSequenceForModel(
          schema
        );

        const createFlowModelData: IFlowModelCommandData = {
          id: this.guidService.empty(),
          name: flowModel.Name,
          description: flowModel.Description,
          customPlaceholder: flowModel.CustomPlaceholder,
          category: flowModel.Category,
          active: flowModel.Active,
          createdById: importable.processOwnerId || flowModel.CreatedById,
          businessId: flowModel.BusinessId,
          currentUserId: flowModel.CreatedById,
          businessName: null,
          createdDate: flowModel.CreatedDate,
          updatedDate: flowModel.UpdatedDate,
          backgroundColour: flowModel.BackgroundColour,
          lastModifiedById: flowModel.CreatedById,
          hasErrors: flowModel.HasErrors,
          processDetail: processDetail,
          nodes: importable.flowinglyNodes,
          flowSchema: JSON.stringify(schema),
          categoryId: importable.categoryId,
          importedId: importable.key,
          importedUniqueId: importable.uniqueId,
          flowModelType: FlowModelType.ProcessMap,
          isAiGenerated: false
        };

        return this.modelerApiService
          .addWorkflow(createFlowModelData)
          .then((workflowResponse) => {
            if (!importable.fileId) {
              return this.fileService
                .uploadFileToFolder(importable.file, FileFolder.FlowModelSource)
                .then((fileResponse) => {
                  importable.fileId = fileResponse.data;
                  return workflowResponse.data?.Id;
                });
            }
            return workflowResponse.data?.Id;
          })
          .then((flowModelId: Guid) => {
            if (!flowModelId || flowModelId === this.guidService.empty()) {
              return;
            }
            return this.flowModelApiService
              .linkFlowModelToSourceFile(flowModelId, importable.fileId)
              .then(() => flowModelId);
          })
          .then((flowModelId) => {
            if (!flowModelId || !importable.publish) {
              return;
            }
            const publishDetails: IPublishWorkflowModel = {
              publishToEveryone: true,
              flowId: flowModelId,
              categoryId: importable.categoryId,
              publishType: FlowModelPublishType.PROCESS_MAP,
              notifyUsers: false,
              isConfidential: false,
              isStepAssistEnabled: true,
              isPublishRequestSchema: false
            };
            return this.flowModelApiService.publishFlowModel(publishDetails);
          });
      });
  }

  private convertImportableNodesToModelNodes(
    importableNodes: IImportableModelNode[],
    tagGroups?: ITagGroupDetail[]
  ) {
    const flatTagGroups = tagGroups?.flatMap((group) => group.tags) || [];
    return importableNodes.map((importableNode) => {
      const modelNode: IModelerNode = {
        ...importableNode,
        NotifyOnStepCreated: false,
        reminderType: 'None',
        deadlineType: 'None',
        costTimeType: 'None',
        webhooks: [],
        noReminder: true,
        ngDialogId: 'ngdialog2',
        isSaved: true,
        isNew: false,
        displayNotificationIcon: 'false',
        tags: tagGroups
          ? (importableNode.tagNames || [])
              .map((tagName) => {
                tagName = this.tagService
                  .normalizeTagName(tagName)
                  .toLowerCase();

                return flatTagGroups.find(
                  (tag) => tag.name.toLowerCase() === tagName
                )?.id;
              })
              .filter(Boolean)
              // make sure tag ids are unique for this node
              .filter((tagId, index, self) => self.indexOf(tagId) === index)
          : []
      };
      return modelNode;
    });
  }
  public normalizeForComparison(type: string, s: string): string {
    return type === 'Video'
      ? s.replaceAll('-', '').replaceAll(' ', '')
      : s.replaceAll(' ', '');
  }

  private applyImportOptions(
    importables: IImportable[],
    options?: IFlowModelImportOptions
  ) {
    const shouldUseReferenceNo = options?.useReferenceNo ?? false;
    const shouldPreferExpert = options?.preferExpert ?? false;
    const importableFlowModels = importables.filter(
      (importable): importable is ImportableFlowModel =>
        importable instanceof ImportableFlowModel
    );

    importableFlowModels.forEach((importableFlowModel) => {
      const processName = this.getTrimmedValue(
        importableFlowModel.sourceProcessName || importableFlowModel.name
      );
      const processReferenceNo = this.getTrimmedValue(
        importableFlowModel.processReferenceNo
      );

      if (shouldUseReferenceNo && processReferenceNo) {
        importableFlowModel.name = processName
          ? `${processReferenceNo} - ${processName}`
          : processReferenceNo;
      } else {
        importableFlowModel.name = processName || '';
      }

      const ownerAttributeName = this.getTrimmedValue(
        importableFlowModel.processOwnerAttributeName
      );
      const expertAttributeName = this.getTrimmedValue(
        importableFlowModel.processExpertAttributeName
      );
      importableFlowModel.processOwnerName = shouldPreferExpert
        ? expertAttributeName || ownerAttributeName
        : ownerAttributeName;
      importableFlowModel.unresolvedProcessOwnerNames = [];
    });
  }

  private getTrimmedValue(value?: string | null) {
    if (!value) {
      return null;
    }

    const trimmedValue = value.trim();
    return trimmedValue.length > 0 ? trimmedValue : null;
  }

  private getUserByFullName<
    TUser extends { firstName: string; lastName: string }
  >(users: TUser[], fullName: string | null): TUser | null {
    if (!fullName) {
      return null;
    }

    return (
      users.find((user) => `${user.firstName} ${user.lastName}` === fullName) ||
      null
    );
  }

  private resolveImportableOwnerFromUsers(
    importable: ImportableFlowModel,
    users: IUserSummary[]
  ): IUserSummary | null {
    const ownerNamesToResolve = this.getOwnerNamesToResolve(importable);
    for (const ownerName of ownerNamesToResolve) {
      const user = this.getUserByFullName(users, ownerName);
      if (user) {
        importable.processOwnerName = ownerName;
        importable.unresolvedProcessOwnerNames = [];
        return user;
      }
    }

    importable.unresolvedProcessOwnerNames = ownerNamesToResolve;

    return null;
  }

  private async resolveImportableOwnerForBusiness(
    importable: ImportableFlowModel
  ): Promise<void> {
    const ownerNamesToResolve = this.getOwnerNamesToResolve(importable);
    const searchOwner = async (
      ownerName: string
    ): Promise<{ id: Guid } | null> => {
      const users = await this.userApiService.searchUsers({
        term: ownerName
      });

      const matchingUser = this.getUserByFullName(users || [], ownerName);
      if (!matchingUser) {
        return null;
      }

      return { id: matchingUser.id };
    };

    try {
      let user: { id: Guid } | null = null;
      for (const ownerName of ownerNamesToResolve) {
        user = await searchOwner(ownerName);
        if (user) {
          importable.processOwnerName = ownerName;
          break;
        }
      }

      importable.processOwnerId = user?.id;
      importable.unresolvedProcessOwnerNames = user ? [] : ownerNamesToResolve;
    } catch {
      importable.processOwnerId = undefined;
      importable.unresolvedProcessOwnerNames = ownerNamesToResolve;
    }
  }

  private getOwnerNamesToResolve(importable: ImportableFlowModel): string[] {
    const ownerNamesToResolve: string[] = [];
    const preferredOwnerName = this.getTrimmedValue(
      importable.processOwnerName
    );
    if (preferredOwnerName) {
      ownerNamesToResolve.push(preferredOwnerName);
    }

    const fallbackOwnerName = this.getTrimmedValue(
      importable.processOwnerAttributeName
    );
    if (fallbackOwnerName && !ownerNamesToResolve.includes(fallbackOwnerName)) {
      ownerNamesToResolve.push(fallbackOwnerName);
    }

    return ownerNamesToResolve;
  }
}

angular
  .module('flowingly.services')
  .service('flowModelImportService', FlowModelImportService);

export type FlowModelImportServiceType = FlowModelImportService;
